import { formatCurrency } from "../lib/format";

/**
 * Signed-amount Display nach Architektur-Entscheidung 2026-06-05.
 *
 * Konvention für die Quittungs-App-UI:
 *   - text-ios-blue → positive Werte (Einnahme: priceGross < 0 wird hier positiv dargestellt, da der
 *     "ich bekomme Geld"-Fall — gleich wie Auswertungen-Σ-Bucket)
 *   - text-ios-red  → negative Werte (Ausgabe: priceGross > 0 wird hier rot dargestellt)
 *   - text-ios-muted → neutral (Saldenverschiebung — Tilgung etc.) und Null-Werte
 *
 * ⛔ Niemals grün als Positiv-Indikator — User hat Rot-Grün-Farbschwäche.
 *    Siehe [[feedback_signed_amount_color_display]].
 *
 * Die Komponente nimmt den ORIGINAL-signed Wert (z.B. -25.00 für Einnahme), entscheidet
 * an einer Stelle über Farbe + Vorzeichen-Strip, und zeigt nur Math.abs() an.
 * CSV-Export bleibt unberührt (anderswo direkt aus dem Datenmodell).
 *
 * @param value          Der signed Wert (Vorzeichen entscheidet Farbe — außer isNeutral=true)
 * @param currency       ISO-Code (EUR, …)
 * @param isNeutral      Wenn true → grau gerendert, kein Farb-Split (Tilgung etc.)
 * @param className      Zusätzliche Klassen (z.B. text-right, tabular-nums ist immer drin)
 * @param treatNegativeAsIncome  Default true. priceGross<0 = Einnahme (blau). Wenn false:
 *                       value>0 = blau, value<0 = rot (für reine Σ-Boxen, die schon abs-aggregiert sind).
 */
export function SignedAmount({
  value,
  currency = "EUR",
  isNeutral = false,
  className = "",
  treatNegativeAsIncome = true,
}: {
  value: number | null;
  currency?: string;
  isNeutral?: boolean;
  className?: string;
  treatNegativeAsIncome?: boolean;
}) {
  if (value === null) return <span className={`tabular-nums text-ios-muted ${className}`}>—</span>;
  // User-Entscheidung 2026-06-05: Neutral wird NICHT grau (kollidiert mit Label-Farbe),
  // sondern Standard-Schrift (text-ios-ink) mit ↔-Präfix als Saldenverschiebungs-Indikator.
  if (isNeutral) {
    return (
      <span className={`tabular-nums text-ios-ink ${className}`.trim()}>
        <span aria-hidden="true">↔ </span>{formatCurrency(Math.abs(value), currency)}
      </span>
    );
  }
  let colorClass: string;
  if (value === 0) {
    colorClass = "text-ios-ink"; // Null = Standard-Schrift (kein Akzent, aber lesbar)
  } else if (treatNegativeAsIncome) {
    // priceGross-Konvention: <0 = Einnahme (blau), >0 = Ausgabe (rot)
    colorClass = value < 0 ? "text-ios-blue" : "text-ios-red";
  } else {
    // Σ-Box-Konvention: positive Einnahme-Summe = blau, positive Ausgabe-Summe rot — Caller flippt selbst
    colorClass = value > 0 ? "text-ios-blue" : "text-ios-red";
  }
  return (
    <span className={`tabular-nums ${colorClass} ${className}`.trim()}>
      {formatCurrency(Math.abs(value), currency)}
    </span>
  );
}
